<template>
  <BasePage :pageTitle="__DICT__.menuOptions.Coaches" show-menu show-footer>
    <div v-for="coachId in coachIds" :key="coachId" class="card mb-2">
      <router-link :to="`/coach/${coachId}`">
        <div class="card-header">
          <div class="card-status-top bg-primary"></div>
          <h4 class="card-title">{{ __DICT__.coach }} {{ coachId }}</h4>
          <div class="card-actions">
            <span class="badge bg-primary-lt border-primary mt-1 mx-1" v-if="isInbound(coachId)">
              {{ __DICT__.inbound }} {{ getPassengersForCoach(coachId, 'inbound').length }} {{ __DICT__.passengers }}
            </span>
            <span class="badge bg-primary text-white mt-1 mx-1" v-if="isOutbound(coachId)">
              {{ __DICT__.outbound }} {{ getPassengersForCoach(coachId, 'outbound').length }} {{ __DICT__.passengers }}
            </span>
          </div>
        </div>
        <div class="card-body">
          <div class="text-muted white-space-pre-line" v-html="formatFirstDeparture(getCoachTransportInfo(coachId))">
          </div>
        </div>
      </router-link>
    </div>

    <div v-if="isLoading" class="text-center mt-3">
      <div class="spinner-border text-primary" role="status"></div>
      <span class="ms-2">{{ __DICT__.loading }}</span>
    </div>
  </BasePage>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useWeekStore } from '@/stores/week.js'
import BasePage from "@/components/BasePage.vue"

const weekStore = useWeekStore()
const weekData = ref([])
const isLoading = ref(true)

const coachPassengers = computed(() => {
  // Helper function to find transfer transport by type
  const findTransfer = (passenger, type) => {
    return passenger.allocPlaces?.find(place => {
      const transport = place.transport
      return transport?.transportType === "coach_travel" && transport?.type === type
    })?.transport
  }

  // Helper function to add passenger to transfer group
  const updateTransferGroup = (transfers, transport, passenger, isInbound) => {
    if (!transport?.coach) return

    if (!transfers[transport.coach]) {
      transfers[transport.coach] = {
        transportInboundInfo: null,
        transportOutboundInfo: null,
        inboundPassengers: [],
        outboundPassengers: []
      }
    }

    const group = transfers[transport.coach]

    if (isInbound) {
      group.transportInboundInfo = transport
      if (!group.inboundPassengers.includes(passenger)) {
        group.inboundPassengers.push(passenger)
      }
    } else {
      group.transportOutboundInfo = transport
      if (!group.outboundPassengers.includes(passenger)) {
        group.outboundPassengers.push(passenger)
      }
    }
  }

  // Main processing logic
  const data = weekData.value || []
  return data.reduce((transfers, passenger) => {
    const transportInbound = findTransfer(passenger, "transport-inbound")
    const transportOutbound = findTransfer(passenger, "transport-outbound")

    if (transportInbound) {
      updateTransferGroup(transfers, transportInbound, passenger, true)
    }
    if (transportOutbound) {
      updateTransferGroup(transfers, transportOutbound, passenger, false)
    }

    return transfers
  }, {})
})

const coachIds = computed(() => {
  return Object.keys(coachPassengers.value).sort()
})

function getPassengersForCoach(coachId, direction) {
  if(direction === 'inbound') {
    return coachPassengers.value[coachId]?.inboundPassengers || []
  }
  return coachPassengers.value[coachId]?.outboundPassengers || []
}

function getCoachTransportInfo(coachId) {
  return [coachPassengers.value[coachId]?.transportInboundInfo, coachPassengers.value[coachId]?.transportOutboundInfo]
}


function isInbound(coachId) {
  return coachPassengers.value[coachId].transportInboundInfo
}

function isOutbound(coachId) {
  return coachPassengers.value[coachId].transportOutboundInfo
}


function formatFirstDeparture(transportInfo) {
  let res = ''
  if(transportInfo[1]) {
    const firstDeparture = transportInfo[1].departures.sort((a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    )[0]
    res += `${firstDeparture.location} - ${firstDeparture.date_time_formatted} ${firstDeparture.date_formatted.replace(/-/g,'/')}`
  }

  if(transportInfo[0]) {
    const firstDeparture = transportInfo[0].departures.sort((a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    )[0]
    if (res.length > 0) res += `<br>`;
    res += `${firstDeparture.location} - ${firstDeparture.date_time_formatted} ${firstDeparture.date_formatted.replace(/-/g,'/')}`
  }

  return res;

}

onMounted(async () => {
  try {
    weekData.value = await weekStore.weekData
  } catch (error) {
    console.error('Error loading coach data:', error)
  } finally {
    isLoading.value = false
  }
})
</script>